import { tierKey } from '../utils/tierStyles';
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { QRCodeSVG } from 'qrcode.react';
import {
  Loader2, Download, Shield, User, Calendar, CreditCard, CheckCircle, RefreshCw
} from 'lucide-react';

const TIER_COLORS = {
  Starter: { bg: 'from-[#1a1a2e] to-[#16213e]', accent: '#00E5FF', badge: 'bg-[#00E5FF]/20 text-[#00E5FF]' },
  Professional: { bg: 'from-[#1a1a2e] to-[#0f3460]', accent: '#7C4DFF', badge: 'bg-[#7C4DFF]/20 text-[#7C4DFF]' },
  Elite: { bg: 'from-[#1a1a2e] to-[#1b1b3a]', accent: '#FFD740', badge: 'bg-[#FFD740]/20 text-[#FFD740]' },
  Inactive: { bg: 'from-[#1a1a2e] to-[#2a2a3e]', accent: '#5C7599', badge: 'bg-white/10 text-[#5C7599]' },
  'N/A': { bg: 'from-[#1a1a2e] to-[#2a2a3e]', accent: '#5C7599', badge: 'bg-white/10 text-[#5C7599]' },
};

export function MembershipCardPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  const [qrExpiresAt, setQrExpiresAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Pulls a freshly-signed 5-min QR token from the server. Auto-refreshes
  // at ~80% of the TTL so the QR a merchant scans is always valid.
  const fetchQrToken = useCallback(async () => {
    try {
      setRefreshing(true);
      const { data } = await api.get('/api/member/qr-token');
      setQrToken(data.token);
      setQrExpiresAt(data.expires_at);
      return data.ttl_seconds || 300;
    } catch (e) {
      console.debug('QR token fetch failed:', e?.message || e);
      return 60;
    } finally {
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get('/api/auth/me');
        setMemberData(data);
        if (data.subscription_plan) {
          try {
            const { data: plans } = await api.get('/api/subscriptions/plans');
            const list = plans.plans || [];
            const p = list.find(pl => pl.id === data.subscription_plan_id || pl.name === data.subscription_plan)
              || list.find(pl => tierKey(pl.name) === tierKey(data.subscription_plan));
            if (p) setPlan(p);
          } catch (e) {
            console.debug('Plan lookup failed for membership card:', e?.message || e);
          }
        }
      } catch (e) {
        console.error('Failed to load member data:', e);
      }
      setLoading(false);
    };
    fetchData();
    fetchQrToken();
  }, [api, fetchQrToken]);

  // Refresh the QR token at 80% of its TTL.
  useEffect(() => {
    if (!qrToken) return;
    const id = setInterval(() => { fetchQrToken(); }, 4 * 60 * 1000);
    return () => clearInterval(id);
  }, [qrToken, fetchQrToken]);

  const downloadQR = () => {
    const svg = document.querySelector('#membership-qr svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      const link = document.createElement('a');
      link.download = `AGA_QR_${memberData?.membership_id || 'card'}.png`;
      link.href = canvas.toDataURL();
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  const membershipId = memberData?.membership_id || 'N/A';
  const tierName = plan?.name || (memberData?.subscription_status === 'active' ? 'Active' : 'Free');
  const tierStyle = TIER_COLORS[tierName] || TIER_COLORS['N/A'];
  const isActive = memberData?.subscription_status === 'active';
  const verifyUrl = `${window.location.origin}/verify/${membershipId}`;
  const selfieUrl = memberData?.selfie_url ? (memberData.selfie_url.startsWith('/') ? memberData.selfie_url : memberData.selfie_url) : '';
  const memberSince = memberData?.created_at ? new Date(memberData.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A';

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="membership-card-page">
        <div className="mb-8 text-center">
          <h1 className="font-outfit text-2xl font-bold text-white">My Membership Card</h1>
          <p className="text-[#A0B4CC] text-sm mt-1">Show this QR code to verify your AGA membership</p>
        </div>

        {/* Membership Card */}
        <Card className={`keep-dark bg-gradient-to-br ${tierStyle.bg} border-white/10 overflow-hidden mb-6`}>
          <CardContent className="p-0">
            <div className="p-6 sm:p-8">
              {/* Card Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Shield className="w-5 h-5" style={{ color: tierStyle.accent }} />
                  </div>
                  <div>
                    <p className="font-outfit text-lg font-bold text-white">ALL GLOBAL ACCESS</p>
                    <p className="text-[#5C7599] text-[10px] uppercase tracking-[0.2em]">Membership Card</p>
                  </div>
                </div>
                {isActive && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tierStyle.badge}`}>
                    {tierName}
                  </span>
                )}
              </div>

              {/* Card Body */}
              <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                {/* Photo + Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-5">
                    {selfieUrl ? (
                      <img
                        src={selfieUrl}
                        alt="Member"
                        className="w-20 h-20 rounded-xl object-cover border-2"
                        style={{ borderColor: tierStyle.accent }}
                        data-testid="member-photo"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center">
                        <User className="w-10 h-10 text-[#5C7599]" />
                      </div>
                    )}
                    <div>
                      <h2 className="font-outfit text-xl font-bold text-white" data-testid="member-name">
                        {memberData?.name || 'Member'}
                      </h2>
                      <p className="text-[#A0B4CC] text-sm">{memberData?.email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Membership ID</p>
                      <p className="text-white font-mono text-sm font-bold" data-testid="membership-id">{membershipId}</p>
                    </div>
                    <div>
                      <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Member Since</p>
                      <p className="text-white text-sm">{memberSince}</p>
                    </div>
                    <div>
                      <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Status</p>
                      <p className={`text-sm font-medium ${isActive ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>
                        {isActive ? 'Active' : 'Free member'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">Tier</p>
                      <p className="text-sm font-medium" style={{ color: tierStyle.accent }}>{tierName}</p>
                    </div>
                  </div>
                </div>

                {/* QR Code — encodes a 5-min signed token so merchants
                    can verify the member identity server-side. */}
                <div className="flex flex-col items-center gap-2" id="membership-qr">
                  <div className="bg-white p-3 rounded-xl relative" data-testid="qr-code">
                    <QRCodeSVG
                      value={qrToken ? JSON.stringify({ v: 1, t: 'aga-member', token: qrToken }) : verifyUrl}
                      size={160}
                      level="H"
                      includeMargin={false}
                      fgColor="#000B18"
                    />
                    {refreshing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-xl">
                        <Loader2 className="w-6 h-6 animate-spin text-[#000B18]" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={fetchQrToken}
                    className="text-[#A0B4CC] hover:text-[#00E5FF] text-[10px] inline-flex items-center gap-1 transition-colors"
                    data-testid="qr-refresh-btn"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>{qrToken ? 'Refreshes every 5 min' : 'Tap to load'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Card Footer */}
            <div className="px-6 sm:px-8 py-3 bg-white/[0.03] border-t border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-[#00E676]" />
                <span className="text-[#5C7599] text-[10px]">Verified Member</span>
              </div>
              <span className="text-[#5C7599] text-[10px] font-mono">{membershipId}</span>
            </div>
          </CardContent>
        </Card>

        {!isActive && (
          <div className="mb-6 rounded-xl border border-[#FFD740]/30 bg-[#FFD740]/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="free-card-upgrade-banner">
            <p className="text-sm text-[#E6EEF8] flex-1">
              <b className="text-[#FFD740]">Free membership:</b> you can browse every merchant and offer. Upgrade to <b>Pro</b> or <b>Elite</b> to get the discounts when you show this card.
            </p>
            <Link to="/my-plan" className="rounded-full bg-[#FFD740] text-[#000B18] font-semibold px-4 py-2 text-sm hover:bg-[#FFE57F] transition-colors text-center" data-testid="free-card-upgrade-btn">Upgrade to unlock discounts</Link>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-3">
          <Button
            onClick={downloadQR}
            className="bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 border border-[#00E5FF]/30"
            data-testid="download-qr-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Download QR Code
          </Button>
        </div>

        {/* Info */}
        <div className="mt-8 p-4 rounded-lg bg-[#041A3B]/50 border border-white/5">
          <p className="text-[#5C7599] text-xs text-center">
            Present this QR code for membership verification. When scanned, it displays your name, photo, membership ID, and subscription tier.
          </p>
        </div>
      </div>
    </MemberSidebarLayout>
  );
}
